import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getClientCampaign } from '../../api/clientPortal';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { Spinner, ErrorBanner } from './ClientDashboardPage';

export default function ClientCampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme: T } = useClientTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    let active = true;
    getClientCampaign(id)
      .then((data) => { if (active) setCampaign(data?.campaign || null); })
      .catch((err) => { if (active) setError(err?.response?.data?.error || 'Failed to load campaign.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  if (loading) return <Spinner T={T} />;
  if (error) return <ErrorBanner T={T}>{error}</ErrorBanner>;
  if (!campaign) return <ErrorBanner T={T}>Campaign not found.</ErrorBanner>;

  const card = { borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18 };
  const sLabel = { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 10 };

  const tierRows = Array.isArray(campaign?.tiers)
    ? campaign.tiers
    : Array.isArray(campaign?.products)
      ? campaign.products.flatMap((p) => (p?.tiers || []).map((t) => ({ ...t, productName: p?.sivadProduct?.title })))
      : [];

  const productImages = Array.from(new Set(
    (campaign?.products || []).flatMap((p) => Array.isArray(p?.sivadProduct?.images) ? p.sivadProduct.images : [])
  ));

  const fmt = (d) => d ? new Date(d).toLocaleDateString() : 'Not set';

  return (
    <div>
      <button onClick={() => navigate('/client-portal/campaigns')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 12, marginBottom: 20, padding: 0 }}>
        <ArrowLeft size={14} /> Campaigns
      </button>

      <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 6 }}>{campaign.name}</div>
      {campaign.description && <div style={{ fontSize: 13, color: T.textSub, marginBottom: 24, lineHeight: 1.5 }}>{campaign.description}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Reservation Open',  value: fmt(campaign?.campaignWindow?.startDate) },
          { label: 'Reservation Close', value: fmt(campaign?.campaignWindow?.endDate) },
          { label: 'Delivery',          value: fmt(campaign?.deliveryDate) },
        ].map(({ label, value }) => (
          <div key={label} style={card}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, marginBottom: 20 }}>
        <div style={sLabel}>Tier Table</div>
        {tierRows.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Tier', 'Product', 'Price', 'Target'].map((h) => (
                  <th key={h} style={{ textAlign: h === 'Price' || h === 'Target' ? 'right' : 'left', padding: '6px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.label, borderBottom: `1px solid ${T.border}` }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tierRows.map((tier, i) => (
                <tr key={tier._id || tier.tierId || i}>
                  <td style={{ padding: '9px 8px', color: T.text,     borderBottom: `1px solid ${T.border}` }}>{tier.name || 'Tier'}</td>
                  <td style={{ padding: '9px 8px', color: T.textSub,  borderBottom: `1px solid ${T.border}` }}>{tier.productName || '—'}</td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', color: T.gold, fontWeight: 700, borderBottom: `1px solid ${T.border}` }}>
                    {Number.isFinite(Number(tier.price)) ? `$${Number(tier.price).toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', color: T.textSub, borderBottom: `1px solid ${T.border}` }}>{tier.reservationTarget ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ fontSize: 13, color: T.textMuted }}>No tier data available.</div>
        )}
      </div>

      {productImages.length > 0 && (
        <div style={card}>
          <div style={sLabel}>Product Images</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
            {productImages.map((url, i) => (
              <img key={`${url}-${i}`} src={url} alt={`Product ${i + 1}`} style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 8, border: `1px solid ${T.border}` }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
